/**
 * WebSocket sync client for browser use
 *
 * Handles connection, authentication, state sync, and reconnection.
 */

import type {
	ClientMessage,
	ServerMessage,
	ConnectionState,
	SessionIdentity,
	SharedState,
	UserInfo,
	SnapshotInfo,
	DeltaPatch,
	Snapshot,
	UserSelection,
} from './sync-types.ts'
import {computeDelta, hasChanges, deepClone} from './delta.ts'

const STORAGE_KEY = 'arcviz:session'
const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 16000, 30000]

/**
 * Generate or retrieve session identity from localStorage
 */
function getOrCreateIdentity(): SessionIdentity {
	const stored = localStorage.getItem(STORAGE_KEY)
	if (stored) {
		try {
			return JSON.parse(stored) as SessionIdentity
		} catch {
			// Invalid stored data, create new
		}
	}

	// Generate new identity
	// Using crypto.getRandomValues for browser-compatible random generation
	const array = new Uint8Array(32)
	crypto.getRandomValues(array)
	const publicKey = Array.from(array)
		.map(b => b.toString(16).padStart(2, '0'))
		.join('')

	const secretArray = new Uint8Array(32)
	crypto.getRandomValues(secretArray)
	const secretKey = Array.from(secretArray)
		.map(b => b.toString(16).padStart(2, '0'))
		.join('')

	const identity: SessionIdentity = {publicKey, secretKey}
	localStorage.setItem(STORAGE_KEY, JSON.stringify(identity))

	return identity
}

/**
 * Generate signature for auth challenge
 * (Simple version - in production use proper crypto)
 */
function generateSignature(publicKey: string, nonce: string): string {
	return btoa(publicKey + nonce).substring(0, 32)
}

export interface SyncClientOptions {
	/** WebSocket URL (e.g., "ws://localhost:8080/ws") */
	url: string
	/** Callback when connection state changes */
	onConnectionChange?: (state: ConnectionState) => void
	/** Callback when presence list updates */
	onPresenceChange?: (users: UserInfo[]) => void
	/** Callback when receiving initial state from joined session */
	onStateInitial?: (state: SharedState, from: string) => void
	/** Callback when receiving state patch */
	onStatePatch?: (patch: DeltaPatch, from: string) => void
	/** Callback when snapshots list updates */
	onSnapshotsChange?: (snapshots: SnapshotInfo[]) => void
	/** Callback when a snapshot is loaded */
	onSnapshotLoaded?: (snapshot: Snapshot) => void
	/** Callback for errors */
	onError?: (code: string, message: string) => void
}

export class SyncClient {
	private options: SyncClientOptions
	private ws: WebSocket | null = null
	private identity: SessionIdentity
	private reconnectAttempt = 0
	private reconnectTimer: ReturnType<typeof setTimeout> | null = null
	private closed = false

	// State
	private _connectionState: ConnectionState = {
		status: 'disconnected',
		lastHeartbeat: null,
		onlineCount: 0,
		sessionId: null,
		error: null,
	}

	private _users: UserInfo[] = []
	private _snapshots: SnapshotInfo[] = []

	// For state sharing
	private _isSharing = false
	private _lastSharedState: SharedState | null = null
	private _syncThrottleMs = 200
	private _syncTimer: ReturnType<typeof setTimeout> | null = null
	private _pendingState: SharedState | null = null

	constructor(options: SyncClientOptions) {
		this.options = options
		this.identity = getOrCreateIdentity()
	}

	// Getters
	get connectionState(): ConnectionState {
		return this._connectionState
	}

	get users(): readonly UserInfo[] {
		return this._users
	}

	get snapshots(): readonly SnapshotInfo[] {
		return this._snapshots
	}

	get sessionId(): string | null {
		return this._connectionState.sessionId
	}

	get isConnected(): boolean {
		return this._connectionState.status === 'authenticated'
	}

	get isSharing(): boolean {
		return this._isSharing
	}

	/**
	 * Connect to the server
	 */
	connect(): void {
		if (this.ws && this.ws.readyState === WebSocket.OPEN) {
			return
		}

		this.closed = false
		this.updateConnectionState({status: 'connecting', error: null})

		try {
			this.ws = new WebSocket(this.options.url)
			this.setupWebSocket(this.ws)
		} catch (err) {
			this.updateConnectionState({
				status: 'error',
				error: err instanceof Error ? err.message : 'Connection failed',
			})
			this.scheduleReconnect()
		}
	}

	/**
	 * Disconnect from the server
	 */
	disconnect(): void {
		this.closed = true

		if (this.reconnectTimer) {
			clearTimeout(this.reconnectTimer)
			this.reconnectTimer = null
		}

		if (this._syncTimer) {
			clearTimeout(this._syncTimer)
			this._syncTimer = null
		}

		if (this.ws) {
			this.ws.close()
			this.ws = null
		}

		this.updateConnectionState({status: 'disconnected'})
	}

	/**
	 * Set user display name
	 */
	setName(name: string): void {
		this.send({type: 'user:setName', name})
	}

	/**
	 * Update selection state
	 */
	setSelection(selection: UserSelection): void {
		this.send({type: 'user:setSelection', selection})
	}

	/**
	 * Start sharing state
	 */
	startSharing(initialState: SharedState): void {
		this._isSharing = true
		this._lastSharedState = deepClone(initialState)
		this.send({type: 'share:state', state: initialState})
	}

	/**
	 * Stop sharing state
	 */
	stopSharing(): void {
		this._isSharing = false
		this._lastSharedState = null
		this.send({type: 'share:stop'})
	}

	/**
	 * Update shared state (throttled, delta only)
	 */
	updateSharedState(newState: SharedState): void {
		if (!this._isSharing) return

		this._pendingState = newState

		// Throttle updates
		if (!this._syncTimer) {
			this._syncTimer = setTimeout(() => {
				this.flushStateUpdate()
				this._syncTimer = null
			}, this._syncThrottleMs)
		}
	}

	private flushStateUpdate(): void {
		if (!this._pendingState || !this._lastSharedState) return

		const patch = computeDelta(this._lastSharedState, this._pendingState)
		if (hasChanges(patch)) {
			this.send({type: 'share:patch', patch})
			this._lastSharedState = deepClone(this._pendingState)
		}
		this._pendingState = null
	}

	/**
	 * Join another user's shared session
	 */
	joinSession(targetSessionId: string): void {
		this.send({type: 'join', targetSessionId})
	}

	/**
	 * Leave current joined session
	 */
	leaveSession(): void {
		this.send({type: 'leave'})
	}

	/**
	 * Save a snapshot
	 */
	saveSnapshot(name: string, isPublic: boolean, state: SharedState): void {
		this.send({type: 'snapshot:save', name, isPublic, state})
	}

	/**
	 * Delete a snapshot
	 */
	deleteSnapshot(snapshotId: string): void {
		this.send({type: 'snapshot:delete', snapshotId})
	}

	/**
	 * Load a snapshot
	 */
	loadSnapshot(snapshotId: string): void {
		this.send({type: 'snapshot:load', snapshotId})
	}

	/**
	 * Set snapshot visibility
	 */
	setSnapshotPublic(snapshotId: string, isPublic: boolean): void {
		this.send({type: 'snapshot:setPublic', snapshotId, isPublic})
	}

	/**
	 * Request snapshots list refresh
	 */
	refreshSnapshots(): void {
		this.send({type: 'snapshots:list'})
	}

	// Private methods

	private setupWebSocket(ws: WebSocket): void {
		ws.onopen = () => {
			this.reconnectAttempt = 0
			this.updateConnectionState({status: 'connected'})

			// Start authentication
			this.send({type: 'auth', publicKey: this.identity.publicKey})
		}

		ws.onmessage = event => {
			try {
				const message = JSON.parse(event.data as string) as ServerMessage
				this.handleMessage(message)
			} catch (err) {
				console.error('Failed to parse message:', err)
			}
		}

		ws.onclose = () => {
			this.ws = null
			this._isSharing = false
			this._lastSharedState = null

			if (!this.closed) {
				this.updateConnectionState({status: 'disconnected'})
				this.scheduleReconnect()
			}
		}

		ws.onerror = () => {
			// Error details come through onclose
		}
	}

	private handleMessage(message: ServerMessage): void {
		switch (message.type) {
			case 'challenge': {
				// Respond to auth challenge
				const signature = generateSignature(this.identity.publicKey, message.nonce)
				this.send({type: 'auth:response', signature})
				break
			}

			case 'auth:success': {
				this.updateConnectionState({
					status: 'authenticated',
					sessionId: message.sessionId,
				})
				break
			}

			case 'auth:failed': {
				this.updateConnectionState({
					status: 'error',
					error: message.reason,
				})
				break
			}

			case 'heartbeat': {
				this.updateConnectionState({
					lastHeartbeat: Date.now(),
					onlineCount: message.onlineCount,
				})
				break
			}

			case 'presence': {
				this._users = [...message.users]
				this.options.onPresenceChange?.(this._users)
				break
			}

			case 'user:join': {
				this._users = [...this._users, message.user]
				this.options.onPresenceChange?.(this._users)
				break
			}

			case 'user:leave': {
				this._users = this._users.filter(u => u.sessionId !== message.sessionId)
				this.options.onPresenceChange?.(this._users)
				break
			}

			case 'user:update': {
				this._users = this._users.map(u => (u.sessionId === message.sessionId ? {...u, ...message.changes} : u))
				this.options.onPresenceChange?.(this._users)
				break
			}

			case 'state:initial': {
				this.options.onStateInitial?.(message.state, message.from)
				break
			}

			case 'state:patch': {
				this.options.onStatePatch?.(message.patch, message.from)
				break
			}

			case 'snapshots:list': {
				this._snapshots = [...message.snapshots]
				this.options.onSnapshotsChange?.(this._snapshots)
				break
			}

			case 'snapshot:saved': {
				// Add to list
				this._snapshots = [message.snapshot, ...this._snapshots]
				this.options.onSnapshotsChange?.(this._snapshots)
				break
			}

			case 'snapshot:deleted': {
				this._snapshots = this._snapshots.filter(s => s.id !== message.snapshotId)
				this.options.onSnapshotsChange?.(this._snapshots)
				break
			}

			case 'snapshot:loaded': {
				this.options.onSnapshotLoaded?.(message.snapshot)
				break
			}

			case 'error': {
				this.options.onError?.(message.code, message.message)
				break
			}
		}
	}

	private send(message: ClientMessage): void {
		if (this.ws && this.ws.readyState === WebSocket.OPEN) {
			this.ws.send(JSON.stringify(message))
		}
	}

	private updateConnectionState(changes: Partial<ConnectionState>): void {
		this._connectionState = {...this._connectionState, ...changes}
		this.options.onConnectionChange?.(this._connectionState)
	}

	private scheduleReconnect(): void {
		if (this.closed) return

		const delay = RECONNECT_DELAYS[Math.min(this.reconnectAttempt, RECONNECT_DELAYS.length - 1)]

		this.reconnectAttempt++

		this.reconnectTimer = setTimeout(() => {
			this.reconnectTimer = null
			this.connect()
		}, delay)
	}
}
